import {
  GraduationCapIcon,
  ClipboardListIcon,
  ActivityIcon,
  SirenIcon,
  ClipboardCheckIcon,
  PresentationIcon,
  BookOpenIcon,
  FileCheck2Icon,
  ImageIcon,
  FolderKanbanIcon,
  type LucideIcon,
} from 'lucide-react';

export const CHECKOUT_URL = 'https://primeros-auxilios-para-instructores.impultienda.ar/checkout';
export const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/mX6JJkpr8cZQ9WhrcXptFV/Diseno_sin_titulo_(65).png';
export const PRODUCT_IMAGE_SIZE = { width: 1330, height: 1290 };
export const PRICE = '$19.990 ARS';
export const CORE_PROMISE = 'Tu próxima capacitación no empieza desde cero.';

export type Crop = { x: number; y: number; w: number; h: number };

export const heroPillars: { label: string; icon: LucideIcon }[] = [
  { label: 'Método para enseñar', icon: GraduationCapIcon },
  { label: 'Protocolos', icon: ClipboardListIcon },
  { label: 'Prácticas', icon: ActivityIcon },
  { label: 'Simulaciones', icon: SirenIcon },
  { label: 'Evaluaciones', icon: ClipboardCheckIcon },
  { label: 'Clases listas', icon: PresentationIcon },
];

export const preparationQuestions = [
  '¿Qué contenidos doy primero?',
  '¿Cómo explico una maniobra para que realmente la entiendan?',
  '¿Cómo organizo la parte práctica?',
  '¿Qué errores tengo que observar?',
  '¿Qué situación puedo utilizar para practicar?',
  '¿Cómo evalúo si el alumno realmente aprendió?',
];

export const systemCycle = ['Preparar', 'Enseñar', 'Entrenar', 'Evaluar'];

export const painPoints = [
  'Tenés los conocimientos, pero necesitás transformarlos en una capacitación clara.',
  'Perdés tiempo buscando material en diferentes fuentes.',
  'Tenés que preparar actividades y prácticas desde cero.',
  'Sabés realizar una maniobra, pero explicar cómo enseñarla requiere otra preparación.',
  'Terminás improvisando preguntas o situaciones para que los alumnos practiquen.',
  'No siempre tenés criterios concretos para detectar errores.',
  'Preparar una evaluación significa sumar todavía más trabajo.',
];

export const imagineItems = [
  'Qué enseñar',
  'En qué orden enseñarlo',
  'Cómo demostrarlo',
  'Qué errores observar',
  'Cómo organizar la práctica',
  'Qué escenario utilizar',
  'Cómo evaluar al alumno',
  'Clases completas que podés adaptar',
];

export const volumes = [
  {
    label: 'Volumen 1',
    title: 'Fundamentos y Método del Instructor',
    headline: 'Aprendé a enseñar, no solamente a explicar.',
    paragraphs: [
      'Una guía pensada para transformar tus conocimientos en una capacitación clara, práctica y estructurada.',
      'Vas a trabajar seguridad de la escena, bioseguridad, evaluación inicial, comunicación, criterio de actuación y especialmente cómo enseñar habilidades prácticas.',
    ],
    listLabel: 'Incluye herramientas para:',
    list: ['Demostrar maniobras', 'Organizar prácticas', 'Trabajar por estaciones', 'Detectar errores', 'Corregir', 'Dar feedback', 'Mejorar el desempeño del alumno'],
    flow: ['Enseñar', 'Demostrar', 'Practicar', 'Corregir'],
    flowSeparator: '·',
    crop: { x: 60, y: 180, w: 350, h: 450 } as Crop,
  },
  {
    label: 'Volumen 2',
    title: 'Protocolos, Maniobras y Habilidades Críticas',
    headline: 'Tené organizado qué enseñar frente a las principales emergencias.',
    paragraphs: [
      'Incluye contenidos sobre RCP, DEA, atragantamiento, hemorragias, heridas, quemaduras, traumatismos, fracturas, emergencias médicas, pediátricas y ambientales, entre otros.',
      'Con un enfoque pensado específicamente para el instructor:',
    ],
    listLabel: 'Además:',
    list: ['Cómo enseñarlo', 'Qué errores observar', 'Cómo practicarlo', 'Cómo evaluarlo'],
    flow: ['Reconocer', 'Decidir', 'Actuar', 'Pedir ayuda', 'Reevaluar'],
    flowSeparator: '→',
    crop: { x: 950, y: 90, w: 345, h: 385 } as Crop,
  },
  {
    label: 'Volumen 3',
    title: 'Entrenamiento, Simulación y Evaluación del Alumno',
    headline: 'Pasá de enseñar una maniobra a comprobar una competencia.',
    paragraphs: [
      'Aprendé a diseñar prácticas progresivas, dirigir simulaciones, observar el desempeño de tus alumnos, detectar errores críticos y evaluar si realmente pueden actuar frente a una situación.',
    ],
    listLabel: 'Incluye herramientas para trabajar:',
    list: ['Prácticas progresivas', 'Simulaciones', 'Observación', 'Feedback', 'Debriefing', 'Evaluación', 'Reentrenamiento', 'Evaluaciones integradoras'],
    flow: ['Practicar', 'Simular', 'Observar', 'Evaluar', 'Corregir'],
    flowSeparator: '·',
    crop: { x: 950, y: 465, w: 360, h: 385 } as Crop,
  },
];

export const bonuses = [
  {
    label: 'Bono 1',
    title: '25 Escenarios de Simulación',
    body: 'Casos preparados para entrenar situaciones como RCP, DEA, atragantamiento, hemorragias, traumatismos, convulsiones y otras emergencias.',
    includesLabel: 'Cada escenario incluye:',
    includes: 'Objetivo + preparación + consigna + acciones esperadas + errores críticos + checklist + debriefing',
    extras: [] as string[],
    flow: 'Elegís el escenario → preparás la situación → tus alumnos practican.',
    crop: { x: 105, y: 875, w: 235, h: 280 } as Crop,
  },
  {
    label: 'Bono 2',
    title: 'Pack Profesional de Evaluaciones',
    body: 'No tengas que crear una evaluación nueva cada vez que das un curso. Incluye banco de preguntas, evaluaciones, claves de respuesta, rúbricas, checklists de habilidades y fichas de seguimiento.',
    includesLabel: 'Para evaluar:',
    includes: 'Conocimiento + toma de decisiones + ejecución práctica',
    extras: [],
    flow: '',
    crop: { x: 335, y: 875, w: 230, h: 280 } as Crop,
  },
  {
    label: 'Bono 3',
    title: '10 Clases Listas para Dictar',
    body: 'El bono que puede ahorrarte horas de preparación. Incluye 10 capacitaciones estructuradas con:',
    includesLabel: '',
    includes: 'Objetivos + duración + materiales + explicación + demostración + práctica + simulación + evaluación + cierre',
    extras: ['Clase Express de 60 minutos', 'Taller Práctico de 2 horas', 'Jornada Intensiva de 6/8 horas'],
    flow: 'Abrís la planificación → preparás los materiales → adaptás la clase a tu grupo.',
    crop: { x: 560, y: 875, w: 225, h: 280 } as Crop,
  },
  {
    label: 'Bono 4',
    title: 'Kit Visual e Imprimible para el Aula',
    body: 'Dejá de perder tiempo diseñando láminas, resúmenes y materiales para tus alumnos. Incluye infografías, algoritmos visuales, fichas de bolsillo, pósters, hojas resumen y tarjetas de repaso relacionadas con las principales maniobras y emergencias.',
    includesLabel: 'Recursos preparados para:',
    includes: 'Proyectar + imprimir + explicar + entregar + reforzar',
    extras: [],
    flow: 'Elegís el material → lo utilizás en tu clase → tus alumnos se llevan un recurso de consulta.',
    crop: { x: 780, y: 875, w: 225, h: 280 } as Crop,
  },
  {
    label: 'Bono 5',
    title: 'Pack de Gestión Profesional del Instructor',
    body: 'Organizá tus capacitaciones de principio a fin sin depender de planillas improvisadas o documentos dispersos. Incluye checklists de preparación, inventario de materiales, registros de asistencia, fichas de capacitación, organización de estaciones, encuestas de satisfacción, control de equipamiento, plantillas de seguimiento y modelos editables de constancias de participación.',
    includesLabel: 'Todo lo que necesitás para gestionar:',
    includes: 'Antes del curso + durante el curso + después del curso',
    extras: [],
    flow: 'Preparás → organizás → registrás → mejorás.',
    crop: { x: 1000, y: 875, w: 235, h: 280 } as Crop,
  },
];

export const productIndicators: { label: string; icon: LucideIcon }[] = [
  { label: '3 volúmenes', icon: BookOpenIcon },
  { label: '25 escenarios', icon: SirenIcon },
  { label: 'Evaluaciones y rúbricas', icon: FileCheck2Icon },
  { label: '10 clases', icon: PresentationIcon },
  { label: 'Recursos visuales', icon: ImageIcon },
  { label: 'Herramientas de gestión', icon: FolderKanbanIcon },
];

export const rebuildTasks = [
  'Volver a abrir diferentes páginas.',
  'Revisar apuntes.',
  'Buscar actividades.',
  'Crear prácticas.',
  'Inventar escenarios.',
  'Diseñar evaluaciones.',
  'Preparar material para los alumnos.',
  'Organizar documentos.',
];

export const offerStack = [
  { tag: 'Volumen 1', name: 'Fundamentos y Método del Instructor' },
  { tag: 'Volumen 2', name: 'Protocolos, Maniobras y Habilidades Críticas' },
  { tag: 'Volumen 3', name: 'Entrenamiento, Simulación y Evaluación del Alumno' },
  { tag: 'Bono 1', name: '25 Escenarios de Simulación' },
  { tag: 'Bono 2', name: 'Pack Profesional de Evaluaciones' },
  { tag: 'Bono 3', name: '10 Clases Listas para Dictar' },
  { tag: 'Bono 4', name: 'Kit Visual e Imprimible para el Aula' },
  { tag: 'Bono 5', name: 'Pack de Gestión Profesional del Instructor' },
];

export const recentBuyerCities = [
  'Córdoba',
  'Rosario',
  'Mendoza',
  'La Plata',
  'San Miguel de Tucumán',
  'Mar del Plata',
  'Salta',
  'Neuquén',
  'Santa Fe',
  'Bahía Blanca',
  'Posadas',
  'Buenos Aires',
  'Corrientes',
  'San Juan',
  'Resistencia',
  'Río Cuarto',
];